/* eslint-disable jsx-a11y/anchor-is-valid */
import { DeleteOutlined, SaveOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Input,
  InputNumber,
  List,
  message,
  Modal,
  Row,
  Select,
  Space,
  Steps,
} from "antd";
import React, { useEffect, useMemo, useReducer, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { getSupaClient, getWorkspace } from "../../lib/supabase";
import { SlackEditor, SlackEditorPreview } from "../TextEditor/SlackEditor";
import ChannelMemberFilter from "../Members/ChannelMemberFilter";

const { Option } = Select;

const { Step } = Steps;

const TriggerTemplate = (props) => {
  // props.trigger
  // props.id
  // props.loadTriggers
  // props.userSettings
  // props.closeModal
  // props.type
  const supabase = getSupaClient();
  const workspace = getWorkspace(supabase);
  const teamId = localStorage.getItem("team-id");
  const slackId = localStorage.getItem("slack-id");

  const [triggerName, setTriggerName] = useState("new trigger");
  const [triggerConfig, setTriggerConfig] = useState([]);
  const [triggerType, setTriggerType] = useState("team_join");
  const [teammates, setTeammates] = useState([]);
  const [currentFilter, setCurrentFilter] = useState();
  const [isBotSelected, setBotSelected] = useState(false);
  const [isWorkHoursSelected, setWorkHoursSelected] = useState(false);

  // Update the configuration object based on key value and index
  function updateConfig(updateKey, updateValue, updateIndex) {
    //Only set trigger config if the new value is different from the old value
    if (_.isEqual(triggerConfig[updateIndex][updateKey], updateValue)) {
      return;
    }

    setTriggerConfig(
      triggerConfig.map((obj, index) => {
        if (index === updateIndex) {
          return {
            ...obj,
            [updateKey]: updateValue,
          };
        }
        return obj;
      })
    );
  }

  /**
   * Purpose: Add steps
   * Params: pass in true if first step
   */
  const addSteps = async (firstStep) => {
    let newTriggerConfig = [];

    // check if first step to set defaults
    newTriggerConfig = [
      ...triggerConfig,
      {
        message: {},
        time: firstStep ? "now" : "custom",
        sendAs: "",
        id: uuidv4(),
        custom_time: firstStep ? null : 7,
      },
    ];

    setTriggerConfig(newTriggerConfig);
  };

  /**
   * Purpose: Delete steps
   * Params: pass in index
   */
  const deleteStep = async (removeIndex) => {
    const newTriggerConfig = triggerConfig.filter(
      (item, index) => index !== removeIndex
    );
    setTriggerConfig(newTriggerConfig);
  };

  /** * Purpose: Verify all objects are preset  */
  const validateConfigObject = async () => {
    let verifyObj = true;
    await triggerConfig.forEach((val) => {
      if (
        val.sendAs === "" ||
        val.sendAs === null ||
        val.message.text === "" ||
        triggerName === ""
      ) {
        verifyObj = false;
      }
      // if custom time, then validate that custom time is present
      if (
        val.time === "custom" &&
        (val.custom_time === "" || val.custom_time === null)
      ) {
        verifyObj = false;
      }
    });
    return verifyObj;
  };

  /**
   * Purpose: save the trigger
   * Also verify if fields exist
   */
  const saveTrigger = async () => {
    // verify all the fields exist
    const verifyObj = await validateConfigObject();

    // if fields not present, show an error
    if (verifyObj) {
      const { data, error } = await supabase.from("team_triggers").insert([
        {
          team: teamId,
          created_by: slackId,
          name: triggerName,
          type: triggerType,
          config: triggerConfig,
          trigger_config:
            triggerType === "channel_join"
              ? {
                  channel: currentFilter.channel,
                }
              : null,
          workspace,
        },
      ]);

      if (error) {
        message.error("Error saving. Please try again");
      }
      if (data) {
        message.success("Saved!");
        window.analytics.track("Trigger saved");
        props.loadTriggers();
        setTriggerName("");
        setTriggerConfig([{ ...triggerConfig[0], id: uuidv4() }]);
        props.closeModal();
      }
    } else {
      message.error("Please fill out all the fields, and try again");
    }
  };

  /**
   * Purpose: edit the trigger
   * Params
   */
  const updateTrigger = async () => {
    // verify all the fields exist
    const verifyObj = await validateConfigObject();

    // if fields not present, show an error
    if (verifyObj) {
      const { data, error } = await supabase
        .from("team_triggers")
        .update([
          {
            created_by: slackId,
            name: triggerName,
            type: triggerType,
            config: triggerConfig,
            updated_at: new Date().toISOString(),
          },
        ])
        .match({ id: props.trigger.id });
      if (error) {
        message.error("Error saving. Please try again");
      }
      if (data) {
        message.success("Saved!");
        props.loadTriggers();
        setTriggerName("");
        window.analytics.track("Trigger updated");
        if (props.closeModal) props.closeModal();
      }
    }
  };

  const deleteTrigger = async () => {
    const { data, error } = await supabase
      .from("team_triggers")
      .delete()
      .match({ team: teamId, id: props.trigger.id });
    if (error) {
      message.error("Error deleting");
    }
    if (data) {
      message.success("Successfully deleted");
    }
    props.loadTriggers();
    window.analytics.track("Trigger deleted");
    if (props.closeModal) props.closeModal();
  };

  const loadTeammates = async () => {
    const { data, error } = await supabase
      .from("users")
      .select("name, slack_id")
      .filter("team", "eq", teamId);

    if (error) {
      console.log("Error loading teammates", error);
      window.analytics.track("Teammates load error");
      return [];
    }
    if (data && data[0]) {
      data.push({ name: "bot", slack_id: "bot" });
      setTeammates(data);
      return data;
    }
    return [];
  };

  useEffect(async () => {
    await loadTeammates();
    if (props.type === "new") {
      addSteps(true);
    }
  }, []);

  useMemo(() => {
    if (props.type === "edit") {
      setTriggerName(props.trigger.name);
      setTriggerConfig(props.trigger.config);
      setCurrentFilter(props.trigger.trigger_config);
      setTriggerType(props.trigger.type);
    }
  }, [props.trigger]);

  return (
    <Space
      key={props.trigger ? props.trigger.id : "new"}
      direction="vertical"
      style={{ width: "100%", textAlign: "left" }}
    >
      <Space
        direction="vertical"
        style={{ textAlign: "left", marginBottom: 10 }}
      >
        <Space>
          <span>Name</span>
          <Input
            placeholder="Enter a name"
            defaultValue={props.trigger ? props.trigger.name : "new trigger"}
            type="text"
            value={triggerName}
            onChange={(e) => setTriggerName(e.target.value)}
          />
        </Space>
        <Space>
          <span>Select a trigger</span>
          <Select
            onChange={(value) => setTriggerType(value)}
            defaultValue={triggerType}
            style={{ width: 280 }}
          >
            <Option value="team_join">When a new member joins</Option>
            <Option value="channel_join">When a member joins a channel</Option>
          </Select>
        </Space>
        <Space>
          {triggerType === "channel_join" ? (
            <Row style={{ marginTop: "1em" }}>
              <ChannelMemberFilter
                teamId={teamId}
                channelJoin
                selected={props.type === "edit" ? currentFilter.channel.id : ""}
                onChange={setCurrentFilter}
              />
            </Row>
          ) : null}
        </Space>
      </Space>
      <Steps direction="vertical">
        {triggerConfig.map((step, index) => (
          <Step
            key={step.id}
            title={`Step ${index + 1}`}
            description={
              <Card style={{ margin: 25 }}>
                <div className="w-full">
                  <div className="flex justify-between">
                    <div>
                      <span>Select an action</span>{" "}
                      {index === 0 ? (
                        <Select
                          style={{ marginLeft: 5 }}
                          defaultValue={
                            props.trigger
                              ? props.trigger.config[index]?.time
                              : "now"
                          }
                          onChange={(value) => {
                            if (value === "work_hours") {
                              if (isBotSelected) {
                                setWorkHoursSelected(true);
                                message.error(
                                  "Cannot send message during work hours as a bot."
                                );
                              } else {
                                setWorkHoursSelected(true);
                                updateConfig("time", value, index);
                              }
                            } else {
                              setWorkHoursSelected(false);
                              updateConfig("time", value, index);
                            }
                          }}
                        >
                          <Option value="now">Send a message in 5 mins</Option>
                          <Option value="day">
                            Send a message after a day
                          </Option>
                          <Option value="work_hours">
                            Send a message during work hours
                          </Option>
                          <Option value="custom">
                            Send a message after X days
                          </Option>
                        </Select>
                      ) : (
                        <Select
                          style={{ marginLeft: 5 }}
                          defaultValue={
                            props.trigger
                              ? props.trigger.config[index]?.time
                              : "custom"
                          }
                          onChange={(value) =>
                            updateConfig("time", value, index)
                          }
                        >
                          <Option value="custom">
                            Send a message after X days
                          </Option>
                        </Select>
                      )}
                    </div>
                    {index !== 0 || triggerConfig[0].time === "custom" ? (
                      <div className="ml-4">
                        After{" "}
                        <InputNumber
                          defaultValue={
                            props.trigger
                              ? props.trigger.config[index]?.custom_time
                              : 7
                          }
                          value={triggerConfig[index]?.custom_time}
                          onChange={(value) =>
                            updateConfig("custom_time", value, index)
                          }
                        />{" "}
                        days
                      </div>
                    ) : null}
                    {teammates ? (
                      <Select
                        style={{ minWidth: 300, marginLeft: 10 }}
                        defaultValue={
                          props.trigger
                            ? props.trigger.config[index]?.sendAs
                            : "Select user to send as"
                        }
                        onChange={(value) => {
                          if (value === "bot") {
                            if (isWorkHoursSelected) {
                              setBotSelected(true);
                              message.error(
                                "Cannot send message as bot during working hours"
                              );
                            } else {
                              setBotSelected(true);
                              updateConfig("sendAs", value, index);
                            }
                          } else {
                            setBotSelected(false);
                            updateConfig("sendAs", value, index);
                          }
                        }}
                      >
                        {teammates.map(function (teammate) {
                          return (
                            <Option
                              key={teammate.slack_id}
                              value={teammate.slack_id}
                              name={teammate.name}
                            >
                              {teammate.name}
                            </Option>
                          );
                        })}
                      </Select>
                    ) : (
                      "Loading"
                    )}
                  </div>
                  <p>
                    <span>Message</span>
                    {props.type === "new" ? (
                      <SlackEditor
                        height="150px"
                        hidePreview
                        onChange={(value) =>
                          updateConfig("message", value, index)
                        }
                        initialText="Hey! :smile:"
                        value={triggerConfig[index]?.message}
                      />
                    ) : (
                      <SlackEditor
                        height="150px"
                        hidePreview
                        onChange={(value) =>
                          updateConfig("message", value, index)
                        }
                        value={triggerConfig[index]?.message}
                      />
                    )}
                  </p>
                  <div className="flex justify-between">
                    <div>
                      <p>
                        Tip: You can use {"{{first_name}}"} and{" "}
                        {"{{@_mention}}"} for personalization!
                      </p>
                    </div>
                    <div>
                      {index !== 0 ? (
                        <Button
                          type="dashed primary"
                          size="small"
                          style={{ borderColor: "#1890ff" }}
                          onClick={() => deleteStep(index)}
                        >
                          - Delete step
                        </Button>
                      ) : null}
                    </div>
                  </div>

                  {/* eslint-disable-next-line no-nested-ternary */}
                  {triggerConfig[index]?.time === "work_hours"
                    ? props.userSettings.work_hours
                      ? `Work hours set from ${props.userSettings.work_hours.start_hour} to ${props.userSettings.work_hours.end_hour}. Timezone: ${props.userSettings.timezone}`
                      : "No working hours set. Please reach out to use through chat or hello@dots.community"
                    : null}
                </div>
              </Card>
            }
          />
        ))}
      </Steps>
      <div style={{ margin: "auto", textAlign: "center" }}>
        <Button
          type="dashed primary"
          style={{ borderColor: "#1890ff" }}
          onClick={() => addSteps(false)}
        >
          + Add another step
        </Button>
      </div>

      <div
        style={{
          marginTop: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {props.type === "edit" ? (
          <div className="flex flex-row space-x-2">
            <Button
              onClick={() => deleteTrigger()}
              size="large"
              block
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
            <Button
              onClick={() => updateTrigger()}
              size="large"
              type="primary"
              block
              icon={<SaveOutlined />}
            >
              Update
            </Button>
          </div>
        ) : (
          <Button
            onClick={saveTrigger}
            style={{ width: "60%" }}
            size="large"
            type="primary"
            block
            disabled={isBotSelected && isWorkHoursSelected}
            icon={<SaveOutlined />}
          >
            Save
          </Button>
        )}
      </div>
    </Space>
  );
};

export default TriggerTemplate;
